import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import * as analytics from 'analytics/Analytics';
import {ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import isArchived from 'archive/isArchived';
import {ACTION_CATEGORY} from 'constants/AnalyticsConstants';
import {getSchemaDisplayNameForCreate} from 'processes/CreateProcessUtil';
import {userCreatableProcessTaskSchema} from 'processes/util/ProcessSchemaConfigUtil';
import {Schema} from 'schemas/SchemaConstants';
import {ConnectedSearchQuery} from 'search/__generated/ConnectedSearchQuery';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import SearchableMenu from 'ui-library/menus/SearchableMenu';

type ProcessTaskSchema = ConnectedSearchQuery['processTaskSchemasSupportingPlatformFeature'][number];

interface CreateFromSchemaButtonProps {
  schemas: Array<Schema> | null;
  onSelect: (schema: Schema) => void;
  isExpanded?: boolean;
}

export default function CreateFromSchemaButton(props: CreateFromSchemaButtonProps): JSX.Element {
  const instantiableSchemas = props.schemas
    ? props.schemas.filter((schema) => {
        const executionType = (schema as ProcessTaskSchema).executionType;
        if (executionType && !userCreatableProcessTaskSchema(schema as ProcessTaskSchema)) {
          return false;
        }

        return !isArchived(schema) && !(schema as ProcessTaskSchema).canOnlyBePartOfFlowchart;
      })
    : [];

  const menu =
    props.schemas == null ? null : instantiableSchemas.length === 0 ? (
      <ul className="dropdown-menu pull-right">
        <li className="disabled">
          <a>No schemas configured</a>
        </li>
      </ul>
    ) : (
      <SearchableMenu
        onSelect={(schema) => {
          analytics.trackEventWithModule(
            {
              module: ANALYTICS_MODULE.WORKFLOWS,
              action: 'selected_schema_for_create_tasks',
              actionCategory: ACTION_CATEGORY.READ,
            },
            ANALYTICS_MODULE.WORKFLOWS,
            {schemaId: schema.id}
          );
          props.onSelect(schema);
        }}
        optionDatas={instantiableSchemas.map((schema) => ({
          value: schema,
          name: getSchemaDisplayNameForCreate(
            schema as ProcessTaskSchema,
            instantiableSchemas as Array<ProcessTaskSchema>
          ),
        }))}
        scrollable={true}
        className={css(styles.pullRight)}
      />
    );

  const tooltipText = menu ? 'Create tasks' : 'Loading...';

  const button = (
    <Button
      buttonStyle={ButtonStyle.SECONDARY}
      disabled={!menu}
      icon="compose"
      size={ButtonSize.SMALL}
      text={props.isExpanded ? 'Create tasks' : undefined}
      dataTestAttrs={{component: 'CreateFromSchemaButton', element: 'root'}}
      aria-label={tooltipText}
    />
  );

  return (
    <div className="u-display-inline-block dropdown">
      {menu ? (
        <Dropdown menu={menu} closeOnInsideClick={true} tooltip={props.isExpanded ? null : tooltipText}>
          {button}
        </Dropdown>
      ) : (
        <Tooltip text={props.isExpanded ? null : tooltipText} placement={FloatingPlacement.BOTTOM}>
          {button}
        </Tooltip>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  pullRight: {
    right: 0,
  },
});
